import type {
  LoaderFunctionArgs,
  ShouldRevalidateFunctionArgs,
} from "react-router";
import { readAnalyticsConsent } from "../../src/analytics/consent";
import { fetchSiteNavigation } from "../../src/marketing/nav-loader";
import { fetchSiteSettings } from "../../src/marketing/site-settings";
import { bootstrapPageDisclaimers } from "../../src/marketing/page-disclaimer";
import { resolveApiEndpoint } from "../../src/marketing/api-endpoint";
import { fetchPageList, pagePresentationLoader } from "../../src/marketing/page-loader";
import type { MarketingLayoutData } from "../../src/layouts/MarketingLayout";

export { MarketingLayout as default } from "../../src/layouts/MarketingLayout";
export { MinimalErrorPage as ErrorBoundary } from "../../src/pages/marketing/ErrorPages";

/**
 * Both of these are resolved here for the same reason: they belong in the
 * initial HTML.
 *
 * The consent choice is a plain cookie — readable on the server. Resolving it
 * here means the initial HTML already reflects a returning visitor's choice:
 * no consent banner is rendered for someone who already consented (and no
 * post-hydration flash when it disappears).
 *
 * The nav is CMS content, and it is the first thing a visitor and every crawler
 * sees. Reading it server-side means the links a visitor gets are the ones the
 * CMS holds, rather than a set that swaps once a client fetch lands.
 *
 * The chrome is the page's template speaking, and it is resolved here for a
 * third reason: the header and footer belong to this layout, and React Router
 * gives a parent route no view of its child's loader data. Both of its reads
 * are memoised per Request, so it shares the fetches the page's own loader
 * makes rather than repeating them.
 *
 * The site settings are the fourth, for the nav's reason: the footer's authored
 * copy is chrome on every page, and a legal notice that swaps in after
 * hydration is a notice the first paint got wrong. The copyright year is stamped here too — resolved once on the server
 * and serialized, so the client's first render cannot disagree with it across a
 * New Year boundary or a timezone split.
 */
export async function loader({
  request,
}: LoaderFunctionArgs): Promise<MarketingLayoutData> {
  const cookie = request.headers.get("cookie") ?? "";
  const analyticsConsent = readAnalyticsConsent(cookie);
  const [nav, presentation, settings] = await Promise.all([
    fetchSiteNavigation(request),
    pagePresentationLoader({ request } as LoaderFunctionArgs),
    fetchSiteSettings(request),
  ]);
  // Share the presentation loader's cached page list when upgrading the legacy
  // sentinel. A real page named `product` keeps its assignment even after moving.
  let resolvedSettings = settings;
  if (settings?.productDisclaimers.some((entry) => entry.pageId === "product")) {
    try {
      const endpoint = await resolveApiEndpoint({ baseUrl: request.url, signal: request.signal });
      const pages = await fetchPageList(endpoint, request);
      if (pages) resolvedSettings = { ...settings, productDisclaimers: bootstrapPageDisclaimers(settings.productDisclaimers, pages) };
    } catch {
      // Keep the settings copy when the page index cannot be read.
    }
  }
  return {
    analyticsConsent,
    primaryNav: nav.primary,
    // The header's call to action and the footer's three columns travel with
    // the links: one read, resolved on the server, so none of the chrome swaps
    // in after hydration.
    cta: nav.cta,
    footerGroups: nav.footer,
    chrome: presentation.chrome,
    ribbon: presentation.ribbon,
    currentPageId: presentation.pageId,
    settings: resolvedSettings,
    copyrightYear: new Date().getFullYear(),
  };
}

/**
 * Re-run on every client-side navigation between pages.
 *
 * This is a pathless layout, so React Router's default only revalidates it when
 * its OWN match changes — which never happens on `/a → /b`. The chrome and the
 * ribbon are the page's template speaking, and they must follow the page a
 * visitor is now on, not the one they arrived at. Both reads are memoised per
 * Request, so the cost is the page fetch the leaf loader makes anyway.
 */
export function shouldRevalidate({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs): boolean {
  return defaultShouldRevalidate || currentUrl.pathname !== nextUrl.pathname;
}
